import io from 'socket.io-client';
import { SOCKET_URL as CONFIG_SOCKET_URL } from '../config';

const isDevelopment = process.env.NODE_ENV === 'development';

// Socket server URL based on environment. Prefer centralized config.
const SOCKET_URL = isDevelopment ? 'http://localhost:90' : CONFIG_SOCKET_URL;

const MMS_NAMESPACE = '/mms';

console.log(`🔧 MMS Web: Environment detected as ${isDevelopment ? 'DEVELOPMENT' : 'PRODUCTION'}`);
console.log(`🔧 MMS Web: Socket URL: ${SOCKET_URL}`);

class MMSSocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.messageHandlers = [];
    this.statusHandlers = [];
    this.assignmentHandlers = [];
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  /**
   * Connect to MMS namespace as artisan
   */
  async connect(user) {
    try {
      if (!user || !user.worker_id) {
        throw new Error('User not authenticated');
      }

      console.log('🔧 MMS Web: Connecting to socket...', user.worker_id);

      // Create socket connection to /mms namespace
      this.socket = io(`${SOCKET_URL}${MMS_NAMESPACE}`, {
        transports: ['websocket'],
        timeout: 10000,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
      });

      this.setupEventListeners(user);
      
      return true;
    } catch (error) {
      console.error('❌ MMS Web: Connection error:', error);
      this.updateStatus('error', error.message);
      return false;
    }
  }

  /**
   * Setup socket event listeners
   */
  setupEventListeners(user) {
    // Connection events
    this.socket.on('connect', () => {
      console.log('✅ MMS Web: Connected to server');
      this.connected = true;
      this.reconnectAttempts = 0;
      this.updateStatus('connected');

      // Join as artisan
      this.socket.emit('mms-join', {
        staffId: user.worker_id.toString(),
        userName: user.worker_name || user.full_name || user.username || 'Artisan',
        department: user.department || 3, // MMS Department
        role: 'artisan' // Artisan role
      });

      console.log('📱 MMS Web: Joined as artisan:', user.worker_name || user.username);
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ MMS Web: Connection error:', error.message);
      this.connected = false;
      this.updateStatus('error', error.message);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('🔌 MMS Web: Disconnected:', reason);
      this.connected = false;
      this.updateStatus('disconnected', reason);
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('🔄 MMS Web: Reconnecting... Attempt:', attemptNumber);
      this.reconnectAttempts = attemptNumber;
      this.updateStatus('reconnecting', attemptNumber);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('✅ MMS Web: Reconnected after', attemptNumber, 'attempts');
      this.connected = true;
      this.reconnectAttempts = 0;
      this.updateStatus('connected');
    });

    this.socket.on('reconnect_failed', () => {
      console.error('❌ MMS Web: Reconnection failed');
      this.updateStatus('failed');
    });

    // MMS-specific events
    this.socket.on('mms-message', (message) => {
      console.log('💬 MMS Web: New message from office:', message);
      this.notifyMessageHandlers(message);
    });

    this.socket.on('mms-assignments', (assignments) => {
      console.log('📋 MMS Web: Job assignments updated:', assignments?.length);
      this.notifyAssignmentHandlers(assignments);
    });

    this.socket.on('mms-job-assigned', (assignment) => {
      console.log('🎯 MMS Web: New job assigned:', assignment);
      this.notifyAssignmentHandlers([assignment]);
    });

    this.socket.on('error', (error) => {
      console.error('❌ MMS Web: Socket error:', error);
      this.updateStatus('error', error);
    });
  }

  /**
   * Send message to office staff
   */
  sendMessage(messageText) {
    if (!this.socket || !this.connected) {
      throw new Error('Not connected to chat server');
    }

    const user = JSON.parse(localStorage.getItem('mms_mobile_user') || '{}');
    if (!user.worker_id) {
      throw new Error('User not authenticated');
    }

    const message = {
      workerId: user.worker_id.toString(),
      workerName: user.full_name || user.username,
      message: messageText.trim(),
      staffId: null, // Artisan sending to office
      userName: user.full_name || user.username,
      timestamp: Math.floor(Date.now() / 1000),
      direction: 0 // Incoming from artisan to office
    };

    console.log('📤 MMS Web: Sending message to office:', message);
    this.socket.emit('mms-send-message', message);

    return message;
  }

  /**
   * Update job status
   */
  updateJobStatus(assignmentId, status, notes = '') {
    if (!this.socket || !this.connected) {
      throw new Error('Not connected to server');
    }

    const user = JSON.parse(localStorage.getItem('mms_mobile_user') || '{}');
    if (!user.worker_id) {
      throw new Error('User not authenticated');
    }

    const update = {
      assignmentId: assignmentId,
      workerId: user.worker_id.toString(),
      status: status, // 'in_progress', 'completed', 'on_hold'
      notes: notes,
      timestamp: Math.floor(Date.now() / 1000)
    };

    console.log('📊 MMS Web: Updating job status:', update);
    this.socket.emit('mms-update-status', update);

    return update;
  }

  /**
   * Send location update
   */
  sendLocationUpdate(latitude, longitude) {
    if (!this.socket || !this.connected) {
      return; // Silently fail for location updates
    }

    const user = JSON.parse(localStorage.getItem('mms_mobile_user') || '{}');
    if (!user.worker_id) return;

    const location = {
      workerId: user.worker_id.toString(),
      latitude: latitude,
      longitude: longitude,
      timestamp: Math.floor(Date.now() / 1000)
    };

    this.socket.emit('mms-location-update', location);
  }

  /**
   * Subscribe to messages
   */
  onMessage(handler) {
    this.messageHandlers.push(handler);
    return () => {
      const index = this.messageHandlers.indexOf(handler);
      if (index > -1) {
        this.messageHandlers.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to connection status changes
   */
  onStatusChange(handler) {
    this.statusHandlers.push(handler);
    // Immediately notify with current status
    handler(this.connected ? 'connected' : 'disconnected');
    
    return () => {
      const index = this.statusHandlers.indexOf(handler);
      if (index > -1) {
        this.statusHandlers.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to job assignments
   */
  onAssignment(handler) {
    this.assignmentHandlers.push(handler);
    return () => {
      const index = this.assignmentHandlers.indexOf(handler);
      if (index > -1) {
        this.assignmentHandlers.splice(index, 1);
      }
    };
  }

  /**
   * Notify all message handlers
   */
  notifyMessageHandlers(message) {
    this.messageHandlers.forEach(handler => {
      try {
        handler(message);
      } catch (error) {
        console.error('Error in message handler:', error);
      }
    });
  }

  /**
   * Notify all status handlers
   */
  updateStatus(status, data = null) {
    this.statusHandlers.forEach(handler => {
      try {
        handler(status, data);
      } catch (error) {
        console.error('Error in status handler:', error);
      }
    });
  }

  /**
   * Notify all assignment handlers
   */
  notifyAssignmentHandlers(assignments) {
    this.assignmentHandlers.forEach(handler => {
      try {
        handler(assignments);
      } catch (error) {
        console.error('Error in assignment handler:', error);
      }
    });
  }

  /**
   * Disconnect from socket
   */
  disconnect() {
    if (this.socket) {
      console.log('🔌 MMS Web: Disconnecting from socket...');
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.updateStatus('disconnected');
    }
  }

  /**
   * Check if connected
   */
  isConnected() {
    return this.connected;
  }
}

// Export singleton instance
export const mmsSocketService = new MMSSocketService();
